import React, {useCallback, useState} from 'react';
import {useLocation} from "react-router-dom";
import ReactModal from "react-modal";
import {CellProps, Column} from "react-table";
import {ListTableWithSearchBox, SearchParams} from "../../common/table/ListTableWithSearchBox";
import {menuMaterialApi} from "../../../../apis/admin/menu/menuMaterialApi";
import {MenuMaterial} from "../../../../types/admin/menuMaterial";
import {getMenuMaterialTypeText} from "../../../../enum/menuMaterial/MenuMaterialType";
import useAppRouter from "../../../../hooks/useAppRouter";
import {formatOmittedDateTime} from "../../../../libs/utils/formatDate";
import {logError} from "../../../../libs/utils/logError";

interface DataType extends MenuMaterial {
  operation?: string
}

interface SelectMaterialModalProps {
  selectedMenuMaterialIdMap: Map<number, boolean>
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSelect: (menuMaterial: MenuMaterial) => Promise<void>;
  onSelected: () => Promise<void>;
}

export const SelectMenuMaterialModal = (props: SelectMaterialModalProps) => {
  const {selectedMenuMaterialIdMap, setShowModal, onSelect, onSelected} = props;
  const [menuMaterialList, setMenuMaterialList] = useState<DataType[]>([]);
  const [abortController, setAbortController] = React.useState<AbortController | undefined>();
  const [totalCount, setTotalCount] = React.useState<number>(10);
  const { goto } = useAppRouter();
  const location = useLocation();

  const sortFunc = async (params: SearchParams) => {
    setAbortController(new AbortController());
    try
    {
      const offset = (params.currentPage - 1) * params.size;
      const response = await menuMaterialApi.index(params.keyword, params.size, offset, params.sort, { abortSignal: abortController?.signal });
      setAbortController(undefined);
      setMenuMaterialList(response.menuMaterialList);
      setTotalCount(response.totalHits);
    } catch(e) {
      await logError(
        location.pathname,
        'メニュー資料一覧の取得に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  const initialSort = [
    'createdAt:desc'
  ];

  const isSelectDisabled = useCallback((men: DataType): boolean => {
    return selectedMenuMaterialIdMap.has(men.id);
  }, [selectedMenuMaterialIdMap]);

  const MaterialTypeCell = ({ value }: CellProps<MenuMaterial>) => {
    return (
      <>
        <div>{ getMenuMaterialTypeText(value) }</div>
      </>
    );
  };

  const DateTimeCell = useCallback(({ value }: CellProps<DataType>) => (
    <div>{ formatOmittedDateTime(value) }</div>
  ), []);

  const OperationCell = useCallback(({ row }: CellProps<DataType>) => {
    const handleSelectClick = async (menuMaterial: MenuMaterial): Promise<void> => {
      await onSelect(menuMaterial);
      await onSelected();
    };

    return (
      <>
        <button
          className={`btn-primary ${ isSelectDisabled(row.original) ? 'btn-primary-disabled' : ''}`}
          type='button'
          onClick={async () => {
            await handleSelectClick(row.original);
          }}
          disabled={isSelectDisabled(row.original)}
        >
          選 択
        </button>
      </>
    );
  }, [onSelect, onSelected, isSelectDisabled]);

  const columns: Column<DataType>[] = React.useMemo(
    () => [
      {
        Header: '名称',
        accessor: 'name',
        className: 'select-none',
      },
      {
        Header: '資料タイプ',
        accessor: 'materialTypeId',
        Cell: MaterialTypeCell,
        disableSortBy: true,
      },
      {
        Header: '作成日時',
        accessor: 'createdAt',
        Cell: DateTimeCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: OperationCell,
        disableSortBy: true,
      },
    ],

    [OperationCell, DateTimeCell]
  );

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        className="select-modal"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">資料選択</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
          </div>
          <div className="form-modal-content">
            <div className="p-4">
              <ListTableWithSearchBox
                {...{
                  tableData: menuMaterialList,
                  columns,
                  initialSort,
                  totalCount,
                  sortFunc,
                }}
              />
            </div>
          </div>
          <div className="form-modal-footer-container">
            <div className="form-modal-footer">
              <button
                className="btn-sub"
                type="button"
                onClick={() => setShowModal(false)}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      </ReactModal>
    </>
  );
};
